import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../contexts/CartContext";

export default function CartDrawer() {
  const { items, isOpen, closeCart, totalCount, totalPrice, savedAmount, updateQuantity, removeItem, clearCart } = useCart();
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCart();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [closeCart]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[60] flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Left accent bar */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#1B4332]" />

        {/* Header */}
        <div className="flex items-center justify-between px-7 h-20 border-b border-zinc-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-[#1B4332] tracking-wide">購物袋</span>
            {totalCount > 0 && (
              <span className="w-6 h-6 flex items-center justify-center bg-[#1B4332] text-white text-xs font-bold rounded-full">
                {totalCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {items.length > 0 && (
              <button
                onClick={clearCart}
                className="text-xs text-zinc-400 hover:text-zinc-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                清空全部
              </button>
            )}
            <button
              onClick={closeCart}
              className="w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-zinc-800 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-7 py-2"
          style={{ scrollbarWidth: "thin", scrollbarColor: "#1B4332 #f4f4f5" }}
        >
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <div className="w-16 h-16 flex items-center justify-center text-zinc-300">
                <i className="ri-shopping-bag-line text-5xl"></i>
              </div>
              <p className="text-zinc-400 text-sm">購物袋是空的</p>
              <button
                onClick={closeCart}
                className="mt-2 px-6 py-2.5 bg-[#1B4332] text-white text-sm rounded-sm hover:bg-zinc-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                去逛逛
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {items.map(({ product, quantity }) => (
                <li key={product.id} className="py-5 flex gap-4">
                  <div className="w-20 h-20 flex-shrink-0 bg-zinc-50 rounded overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-zinc-400 tracking-widest mb-0.5">{product.brand}</p>
                    <p className="text-sm text-[#1B4332] font-medium leading-snug line-clamp-2 mb-2">
                      {product.name}
                    </p>
                    <p className="text-xs text-zinc-400 mb-3">
                      NT$ {product.price.toLocaleString()}
                    </p>
                    {/* Quantity */}
                    <div className="flex items-center gap-0">
                      <button
                        onClick={() => updateQuantity(product.id, quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center border border-zinc-200 text-zinc-500 hover:border-zinc-800 hover:text-zinc-800 transition-colors duration-150 rounded-sm cursor-pointer"
                      >
                        <i className={`text-sm ${quantity === 1 ? "ri-delete-bin-line" : "ri-subtract-line"}`}></i>
                      </button>
                      <span className="w-10 text-center text-sm font-medium text-[#1B4332]">
                        {quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(product.id, quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center border border-zinc-200 text-zinc-500 hover:border-zinc-800 hover:text-zinc-800 transition-colors duration-150 rounded-sm cursor-pointer"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between flex-shrink-0">
                    <button
                      onClick={() => removeItem(product.id)}
                      className="w-7 h-7 flex items-center justify-center text-zinc-300 hover:text-red-400 transition-colors duration-150 cursor-pointer"
                    >
                      <i className="ri-close-line text-base"></i>
                    </button>
                    <span className="text-sm font-semibold text-[#1B4332]">
                      NT$ {(product.price * quantity).toLocaleString()}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="flex-shrink-0 px-7 py-5 border-t border-zinc-100">
            {savedAmount > 0 && (
              <div className="flex items-center gap-2 bg-emerald-50 rounded px-3 py-2.5 mb-4">
                <i className="ri-check-line text-emerald-500 text-sm"></i>
                <span className="text-emerald-700 text-xs font-medium">
                  已為您節省 NT$ {savedAmount.toLocaleString()}
                </span>
              </div>
            )}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm text-zinc-400">
                <span>小計</span>
                <span>NT$ {totalPrice.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm text-zinc-400">
                <span>運費</span>
                <span className="text-emerald-600">免運</span>
              </div>
            </div>
            <div className="flex justify-between items-center border-t border-zinc-200 pt-3 mb-5">
              <span className="font-semibold text-[#1B4332]">總計</span>
              <span className="text-xl font-bold text-[#1B4332]">
                NT$ {totalPrice.toLocaleString()}
              </span>
            </div>
            <Link
              to="/checkout"
              onClick={closeCart}
              className="block w-full h-14 flex items-center justify-center bg-[#1B4332] text-white text-sm font-medium tracking-widest hover:bg-zinc-700 transition-colors duration-200 rounded-sm cursor-pointer whitespace-nowrap"
            >
              前往結帳 →
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
